import { get } from "lodash";

import { SHADOW } from "@treelab/panda";

import { CalculationType, IChartFormValue, StatisticsType } from "../types/form-type";
import { ITableData, IWrokspaceData } from "../types/table-type";

import BasicBarChart from "./BasicBarChart";
import CalculationDataByChart from "./CalculationDataByChart";

import { ChartType } from "../types/global-type";
import { ITooltipFormatterParams } from "../types/echarts-type";

import {
  getBasicDataset,
  getBarTooltipDom,
  getXAxisData,
  getTooltipPosition,
  getRealColumn,
} from "../utils/option_main";
import { getGridWidth } from "../utils/option_secondary";

import { SERIES_AXIS_LABEL_MIN_WIDTH } from "../constants/chart";

class StackBarChart extends BasicBarChart {
  public getDataset(
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData,
    calculation: CalculationDataByChart
  ) {
    const dataset = getBasicDataset(
      formValue,
      tableData,
      workspaceData,
      ChartType.STACKED_COLUMN_BAR,
      this.calculation
    );
    if (Array.isArray(dataset)) {
      const transformList = dataset
        .filter((item) => !!get(item, "transform"))
        .map((item) => get(item, "transform.config.value"));
      this.stackTypeList = transformList;
    } else {
      this.stackTypeList = [];
    }
    return dataset;
  }

  public getTooltip() {
    const canvasWidth = this.getCanvasWidth();
    const tooltip = {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderWidth: 0,
      padding: [10, 12],
      extraCssText: SHADOW.overlay1,
      formatter: (params: ITooltipFormatterParams[] | ITooltipFormatterParams, ticket: string) => {
        const newParams = this.convertTooltipParams(params, true).reverse();
        const dom = getBarTooltipDom(
          newParams,
          canvasWidth,
          this.formValue.chartType,
          this.formatValue,
          this.calculation.isGroup,
          this.checkShowLegendTotal()
        );
        return dom;
      },
      axisPointer: {
        type: "none",
      },
      position: getTooltipPosition,
    };
    return tooltip;
  }

  public getSeries(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isShowLabel, chartType, isShowYAxis } = formValue;
    let label: any = { show: false };
    if (isShowLabel) {
      // TODO: data.length 的计算逻辑待优化
      const data = getXAxisData(get(this.datasetObj, "[0].source"));
      const xAxisWidth = getGridWidth(this.getCanvasWidth(), this.isExpand, chartType, !!isShowYAxis, true);
      if (xAxisWidth > data.length * SERIES_AXIS_LABEL_MIN_WIDTH) {
        label = {
          show: true,
          formatter: (params: { value: string | number[] }) => {
            const { value: data } = params;
            const value = this.formatValue(data[1] as number);
            return value;
          },
          color: "#fff",
          textBorderColor: "inherit",
          textBorderWidth: 1,
        };
      }
    }
    const basicConfig = {
      label: label,
      type: "bar",
      itemStyle: {
        borderRadius: 2,
      },
      animationDuration: 600,
    };
    if (!this.stackTypeList.length) {
      return [basicConfig];
    }
    const seriesList = this.stackTypeList.map((item, index) => ({
      ...basicConfig,
      name: item,
      datasetIndex: index + 1,
      stack: "1",
      itemStyle: {
        borderRadius: 0,
      },
      encode: { x: 0, y: 1 },
    }));
    return seriesList;
  }

  public checkShowLegendTotal(): boolean {
    const { calculationType, calculationTargetId, statisticsType } = this.formValue;
    if (statisticsType === StatisticsType.COUNT) return true;
    const calcuColumn = getRealColumn(calculationTargetId || "", this.tableData);
    if (!calcuColumn) return true;
    return calculationType === CalculationType.SUM;
  }
}

export default StackBarChart;
